import { createFileRoute } from "@tanstack/react-router";
import { SubPage } from "@/components/sub-page";

export const Route = createFileRoute("/legal/politica-cookies")({
  head: () => ({
    meta: [
      { title: "Política de Cookies | Mine Class" },
      {
        name: "description",
        content:
          "Política de cookies de Mine Class: tipos de cookies, gestión del consentimiento y transferencias internacionales conforme a la Ley 21.719.",
      },
      { property: "og:title", content: "Política de Cookies | Mine Class" },
      { property: "og:description", content: "Cómo Mine Class utiliza cookies propias y de terceros, y cómo gestionar tu consentimiento." },
      { property: "og:type", content: "article" },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SubPage
      eyebrow="Legal"
      title="Política de Cookies"
      intro="Última actualización: 20 de julio de 2026"
    >
      <h2>1. ¿Qué son las cookies?</h2>
      <p>
        Las cookies son pequeños archivos de texto que los sitios web almacenan en tu navegador o dispositivo para recordar información sobre tu visita: preferencias, inicio de sesión, mediciones de tráfico y origen de la navegación. Constituyen un estándar tecnológico habitual para el correcto funcionamiento de las plataformas educativas modernas.
      </p>

      <h2>2. Marco normativo aplicable</h2>
      <p>
        En cuanto su uso implique tratamiento de datos personales, las cookies se rigen por la Ley N° 19.628, sobre protección de la vida privada, cuyo texto fue sustituido por la Ley N° 21.719, que regula la protección y el tratamiento de los datos personales y crea la Agencia de Protección de Datos Personales, con entrada en vigencia fijada para el 1 de diciembre de 2026. Esta Política ha sido elaborada anticipadamente conforme al estándar de la Ley N° 21.719; mientras dicha ley no entre en vigencia, se aplican supletoriamente las disposiciones de la Ley N° 19.628 en su texto actual.
      </p>
      <p>
        Esta Política de Cookies forma parte del marco legal del Sitio y debe leerse en conjunto con nuestra Política de Privacidad y nuestros Términos y Condiciones.
      </p>

      <h2>3. Cookies que utilizamos</h2>
      <p>
        Nuestra plataforma implementa distintos tipos de cookies para asegurar una óptima experiencia de usuario y medir el alcance de nuestras comunicaciones. Las clasificamos de la siguiente forma:
      </p>

      <h3>A. Cookies propias (estrictamente necesarias)</h3>
      <ul>
        <li><strong>Autenticación y sesión:</strong> permiten acceder al aula virtual, mantener iniciada la sesión de estudiante o administrador de forma segura y registrar el avance curricular.</li>
        <li><strong>Preferencias de consentimiento:</strong> recuerdan tus decisiones sobre el uso de cookies para no mostrarte el banner de forma recurrente.</li>
        <li><strong>Procesamiento de compras:</strong> permiten la retención técnica de los cursos seleccionados en el carro de compras durante el proceso de pago.</li>
      </ul>
      <p><em>Estas cookies no requieren consentimiento previo, por ser indispensables para prestar el servicio expresamente solicitado por el usuario. Todas las demás categorías se instalan únicamente si las autorizas en el gestor de consentimiento.</em></p>

      <h3>B. Cookies de analítica y rendimiento (requieren consentimiento previo)</h3>
      <ul>
        <li><strong>Google Analytics:</strong> mide el tráfico del Sitio de forma agregada (páginas vistas, tipo de dispositivo, país, páginas de destino y fuente de tráfico mediante parámetros UTM), para comprender cómo interactúan los estudiantes con el contenido educativo.</li>
        <li><strong>Reproductores de video (Vimeo, Wistia o YouTube):</strong> registran la calidad de reproducción y el progreso del video de las clases para garantizar la continuidad pedagógica.</li>
      </ul>

      <h3>C. Cookies publicitarias y de conversión (requieren consentimiento previo)</h3>
      <ul>
        <li><strong>Píxeles de seguimiento (Meta Pixel, Google Ads):</strong> registran si completaste una acción en el Sitio (por ejemplo, registrarte o iniciar un proceso de compra) tras hacer clic en uno de nuestros anuncios en redes sociales o buscadores, con el fin de medir y optimizar nuestras campañas.</li>
        <li><strong>Elaboración de perfiles:</strong> estas cookies pueden implicar la elaboración de perfiles comerciales a partir de tus intereses de navegación, con el fin de mostrarte ofertas de cursos afines. Puedes retirar tu consentimiento u oponerte a este perfilado en cualquier momento, conforme a la sección de derechos de nuestra Política de Privacidad.</li>
      </ul>

      <h2>4. Detalle de las cookies utilizadas</h2>
      <div className="overflow-x-auto">
        <table>
          <thead>
            <tr>
              <th>Cookie</th>
              <th>Titular</th>
              <th>Finalidad</th>
              <th>Duración</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>PHPSESSID (sesión)</td>
              <td>Mine Class SpA</td>
              <td>Autenticación y mantención de la sesión</td>
              <td>Sesión</td>
            </tr>
            <tr>
              <td>cookieconsent_status</td>
              <td>Mine Class SpA</td>
              <td>Registro de preferencias del gestor de consentimiento</td>
              <td>12 meses</td>
            </tr>
            <tr>
              <td>_ga / _gid</td>
              <td>Google LLC</td>
              <td>Analítica de tráfico agregada</td>
              <td>Hasta 24 meses</td>
            </tr>
            <tr>
              <td>_fbp</td>
              <td>Meta Platforms, Inc.</td>
              <td>Publicidad y medición de conversiones</td>
              <td>3 meses</td>
            </tr>
            <tr>
              <td>Vimeo / Wistia / YouTube</td>
              <td>Proveedores respectivos</td>
              <td>Reproducción de video y registro de progreso</td>
              <td>Según proveedor</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>5. Gestión de cookies y consentimiento granular (CMP)</h2>
      <p>
        Al ingresar por primera vez a mine-class.com verás un banner interactivo (gestor de consentimiento o CMP) que te permitirá, de manera previa, libre, específica e informada: (i) aceptar todas las cookies (esenciales, analíticas y publicitarias); (ii) rechazar todas las cookies no esenciales, bloqueando de forma automática las de analítica y marketing; o (iii) configurar de forma granular tus preferencias específicas de privacidad. Las opciones de aceptar y rechazar se presentan con igual visibilidad y facilidad de acceso.
      </p>
      <p>
        Tu decisión quedará registrada por un plazo de 12 meses, transcurrido el cual el banner volverá a consultarte. Puedes modificarla en cualquier momento mediante el enlace "Configuración de Cookies" disponible en el pie de página del Sitio, o bien configurar directamente tu navegador para bloquear o eliminar cookies (Google Chrome, Mozilla Firefox, Safari, Microsoft Edge).
      </p>

      <h2>6. Cookies de terceros y transferencias internacionales</h2>
      <p>
        Las cookies de terceros (Google, Meta y plataformas de video) implican que dichos proveedores pueden tratar los datos recolectados desde servidores ubicados fuera de Chile, principalmente en Estados Unidos y la Unión Europea. Estas transferencias internacionales se sujetan a las garantías descritas en la cláusula de Transferencia Internacional de Datos de nuestra Política de Privacidad.
      </p>

      <h2>7. Cambios en esta política</h2>
      <p>
        Podemos actualizar esta Política de Cookies para reflejar cambios en nuestras herramientas analíticas o nuevos servicios implementados. Cualquier modificación sustancial se notificará mediante un aviso destacado en la plataforma, con indicación de la nueva fecha de actualización.
      </p>

      <h2>8. Contacto</h2>
      <p>
        Para consultas técnicas generales puedes escribir a <a href="mailto:hola@mine-class.com">hola@mine-class.com</a>. Para consultas sobre privacidad y datos personales, escribe a <a href="mailto:privacidad@mine-class.com">privacidad@mine-class.com</a>. Toda referencia al dominio mine-class.cl o a los correos asociados se entiende efectuada a Mine Class SpA.
      </p>
    </SubPage>
  );
}
